import React, { useEffect, useState } from 'react';
import { Save, Check, Download, FileSignature } from 'lucide-react';
import { legalApi } from '../../services/api';

const TEXT_TYPES = [
  { key: 'impressum', label: 'Impressum' },
  { key: 'datenschutz', label: 'Datenschutz' },
  { key: 'agb', label: 'AGB' },
  { key: 'avv', label: 'AVV' },
] as const;

type TextType = typeof TEXT_TYPES[number]['key'];

interface LegalTextData {
  title: string;
  content: string;
  version: number;
}

const TEMPLATES: Record<string, { title: string; content: string }> = {
  impressum: {
    title: 'Impressum',
    content: `Angaben gemäß § 5 TMG

[Ihr Name]
[Ihre Firma / Praxisname]
[Straße + Hausnummer]
[PLZ Ort]

Kontakt:
E-Mail: [ihre@email.de]
Telefon: [Ihre Telefonnummer]

Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV:
[Ihr Name]

Haftungsausschluss:
Die Inhalte dieser Plattform wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte übernehmen wir jedoch keine Gewähr.

Streitschlichtung:
Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: https://ec.europa.eu/consumers/odr/`,
  },
  datenschutz: {
    title: 'Datenschutzerklärung',
    content: `Datenschutzerklärung gemäß Art. 13 DSGVO

1. Verantwortlicher
[Ihr Name], [Ihre Firma]
E-Mail: [ihre@email.de]

2. Erhobene Daten
Bei der Nutzung von CoachDesk365 werden folgende Daten verarbeitet:
- Bestandsdaten (Name, E-Mail-Adresse)
- Nutzungsdaten (Zugriffszeiten, besuchte Seiten)
- Coaching-Daten (Sitzungsprotokolle, Notizen, Rechnungen)

3. Rechtsgrundlage
Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung), Art. 6 Abs. 1 lit. a DSGVO (Einwilligung) und Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse).

4. Speicherdauer
Personenbezogene Daten werden gelöscht, sobald der Zweck der Speicherung entfällt. Gesetzliche Aufbewahrungsfristen bleiben unberührt.

5. Ihre Rechte
Auskunft (Art. 15), Berichtigung (Art. 16), Löschung (Art. 17), Einschränkung (Art. 18), Datenübertragbarkeit (Art. 20), Widerspruch (Art. 21 DSGVO).

6. Cookies
Diese Plattform verwendet ausschließlich technisch notwendige Cookies.

7. Kontakt
Bei Fragen zum Datenschutz: [ihre@email.de]`,
  },
  agb: {
    title: 'Allgemeine Geschäftsbedingungen',
    content: `Allgemeine Geschäftsbedingungen (AGB) für CoachDesk365

1. Geltungsbereich
Diese AGB gelten für die Nutzung der Plattform CoachDesk365.

2. Vertragsgegenstand
CoachDesk365 stellt eine webbasierte Software zur Verwaltung von Coaching-Praxen bereit.

3. Datenschutz
Der Nutzer ist als Verantwortlicher im Sinne der DSGVO für die eingegebenen Klientendaten verantwortlich. Ein AVV kann in den Einstellungen unterzeichnet werden.

4. Kündigung
Das Konto kann jederzeit gelöscht werden. Bei Löschung werden alle Daten DSGVO-konform entfernt.

Stand: Februar 2026`,
  },
  avv: {
    title: 'Auftragsverarbeitungsvertrag (AVV)',
    content: `Auftragsverarbeitungsvertrag gemäß Art. 28 DSGVO

## 1. Gegenstand und Dauer
Dieser Vertrag regelt die Auftragsverarbeitung personenbezogener Daten durch CoachDesk365 im Auftrag des Coaches.

## 2. Art und Zweck der Verarbeitung
Verarbeitung von Klientendaten zum Zweck der Coaching-Praxisverwaltung (Terminplanung, Dokumentation, Rechnungsstellung).

## 3. Art der personenbezogenen Daten
- Kontaktdaten (Name, E-Mail, Telefon)
- Coaching-Daten (Sitzungsprotokolle, Notizen)
- Abrechnungsdaten (Rechnungen, Zahlungen)

## 4. Pflichten des Auftragsverarbeiters
- Verarbeitung nur auf dokumentierte Weisung
- Vertraulichkeitsverpflichtung aller Mitarbeiter
- Technische und organisatorische Maßnahmen (TOMs)
- Unterstützung bei Betroffenenrechten
- Löschung nach Vertragsende

## 5. Technische und organisatorische Maßnahmen
- Verschlüsselung der Daten bei Übertragung (TLS)
- Zugriffskontrolle durch Authentifizierung
- Regelmäßige Sicherheitsupdates
- Datensicherung

## 6. Unterauftragsverarbeiter
Der Einsatz von Unterauftragsverarbeitern erfolgt nur mit vorheriger Zustimmung.

## 7. Rechte des Verantwortlichen
Kontrollrechte, Weisungsrecht, Auskunftsrecht.

## 8. Laufzeit und Kündigung
Der AVV gilt für die Dauer der Nutzung von CoachDesk365.`,
  },
};

export default function LegalSettings() {
  const [activeType, setActiveType] = useState<TextType>('impressum');
  const [texts, setTexts] = useState<Record<string, LegalTextData>>({});
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // AVV status
  const [avvStatus, setAvvStatus] = useState<{ signed: boolean; avv_version?: number; signed_at?: string }>({ signed: false });
  const [signing, setSigning] = useState(false);

  useEffect(() => {
    loadTexts();
    loadAvvStatus();
  }, []);

  useEffect(() => {
    const t = texts[activeType];
    if (t) {
      setEditTitle(t.title);
      setEditContent(t.content);
    } else {
      const tmpl = TEMPLATES[activeType];
      setEditTitle(tmpl?.title || '');
      setEditContent(tmpl?.content || '');
    }
    setSaved(false);
    setError('');
  }, [activeType, texts]);

  const loadTexts = async () => {
    try {
      const res = await legalApi.getTexts();
      const map: Record<string, LegalTextData> = {};
      for (const t of res.data) {
        if (!map[t.text_type] || t.version > map[t.text_type].version) {
          map[t.text_type] = { title: t.title, content: t.content, version: t.version };
        }
      }
      setTexts(map);
    } catch {}
  };

  const loadAvvStatus = async () => {
    try {
      const res = await legalApi.getAvvStatus();
      setAvvStatus(res.data);
    } catch {}
  };

  const handleSave = async () => {
    setSaving(true); setError(''); setSaved(false);
    try {
      await legalApi.updateText(activeType, { title: editTitle, content: editContent });
      setSaved(true);
      await loadTexts();
    } catch {
      setError('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleSignAvv = async () => {
    if (!texts['avv']?.content) {
      setError('Bitte zuerst einen AVV-Text erstellen und speichern.');
      return;
    }
    setSigning(true); setError('');
    try {
      await legalApi.signAvv();
      await loadAvvStatus();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Unterzeichnen');
    } finally {
      setSigning(false);
    }
  };

  const handleDownloadAvv = async () => {
    try {
      const res = await legalApi.downloadAvv();
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `AVV_v${avvStatus.avv_version || 1}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Download fehlgeschlagen');
    }
  };

  return (
    <div className="card" style={{ maxWidth: 720, padding: 32 }}>
      {/* Sub-chips */}
      <div className="category-chips mb-24">
        {TEXT_TYPES.map(({ key, label }) => (
          <button
            key={key}
            className={`chip ${activeType === key ? 'chip-active' : ''}`}
            onClick={() => setActiveType(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ background: 'var(--danger-bg, #fef2f2)', color: 'var(--danger-text, #991b1b)', padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: '0.9rem' }}>
          {error}
        </div>
      )}
      {saved && (
        <div style={{ background: 'var(--success-bg, #e8f5e9)', color: 'var(--success-text, #2e7d32)', padding: '10px 16px', borderRadius: 8, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem' }}>
          <Check size={16} /> Gespeichert (Version {texts[activeType]?.version || 1})
        </div>
      )}

      {/* Text Editor */}
      <div className="form-group">
        <label>Titel</label>
        <input
          value={editTitle}
          onChange={(e) => { setEditTitle(e.target.value); setSaved(false); }}
          placeholder={`${TEXT_TYPES.find((t) => t.key === activeType)?.label || ''} Titel`}
        />
      </div>
      <div className="form-group">
        <label>Inhalt</label>
        <textarea
          value={editContent}
          onChange={(e) => { setEditContent(e.target.value); setSaved(false); }}
          rows={16}
          placeholder="Rechtstext hier eingeben..."
          style={{ fontFamily: 'inherit', lineHeight: 1.6 }}
        />
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving || !editContent}>
          <Save size={16} /> {saving ? 'Speichern...' : 'Speichern'}
        </button>

        {/* AVV-specific actions */}
        {activeType === 'avv' && (
          <>
            <button
              className="btn btn-secondary"
              onClick={handleSignAvv}
              disabled={signing}
            >
              <FileSignature size={16} /> {signing ? 'Wird unterzeichnet...' : 'AVV unterzeichnen'}
            </button>

            {avvStatus.signed && (
              <button className="btn btn-secondary" onClick={handleDownloadAvv}>
                <Download size={16} /> AVV-PDF herunterladen
              </button>
            )}
          </>
        )}
      </div>

      {/* AVV Status */}
      {activeType === 'avv' && avvStatus.signed && (
        <div style={{
          marginTop: 16, padding: '12px 16px', background: 'var(--success-bg, #e8f5e9)', borderRadius: 8,
          fontSize: '0.85rem', color: 'var(--success-text, #2e7d32)',
        }}>
          <Check size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          AVV Version {avvStatus.avv_version} unterzeichnet am{' '}
          {new Date(avvStatus.signed_at!).toLocaleDateString('de-DE', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
          })}
        </div>
      )}

      {/* Hint about public URL */}
      {activeType !== 'avv' && (
        <p className="text-muted text-sm" style={{ marginTop: 16 }}>
          Nach dem Speichern ist der Text unter <code>/{activeType}</code> öffentlich erreichbar.
        </p>
      )}
    </div>
  );
}
